/**
 * Geometry for drag-to-reorder in the scenes grid.
 *
 * Angular CDK's drop lists sort along a single axis (`DropListOrientation` is 'horizontal' |
 * 'vertical' up to CDK 16 — grid-aware 'mixed' only landed in 17), so they cannot sort a wrapped
 * 4-across grid. These helpers do that part; the component owns the pointer plumbing.
 *
 * Deliberately DOM-free so the arithmetic is unit-testable: the component measures the tiles once
 * at drag start and passes plain rects in.
 */

export interface TileRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface GridMetrics {
  /** Origin of the first tile, in the same coordinate space as the points passed to the helpers. */
  left: number;
  top: number;
  cols: number;
  /** Column stride: tile width + gap (not the tile width). */
  cellW: number;
  /** Row stride: tile height + gap. */
  cellH: number;
  /** The tile itself, which is smaller than the cell by the grid gap. */
  tileW: number;
  tileH: number;
}

/** The box a drag is confined to — the grid container's own size. */
export interface GridBox {
  width: number;
  height: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

// Negating a zero origin yields -0, which is equal to 0 but not identical to it — enough to fail a
// strict comparison in a caller or a test for no useful reason. Collapse it to a plain 0.
const unsigned = (v: number): number => (v === 0 ? 0 : v);

/**
 * Derive the grid shape from the tiles' measured rects. The grid is uniform (CSS
 * `repeat(4, minmax(0, 1fr))` + a single gap), so the first row gives the column count and the
 * first two tiles / rows give the strides. Returns null when there is nothing to measure.
 */
export function gridMetrics(rects: TileRect[]): GridMetrics | null {
  if (!rects.length) { return null; }
  const first = rects[0];
  // Tiles on the first row share its top; half a tile height is a generous tolerance for the
  // sub-pixel differences getBoundingClientRect() reports.
  const cols = rects.filter(r => Math.abs(r.top - first.top) < first.height / 2).length;
  // Zero-height tiles make that predicate false for every rect INCLUDING first, so the count comes
  // back 0 — and every fallback below, plus `slot % cols` in the two helpers, assumes at least one
  // column. Reporting "unmeasurable" beats handing the caller NaN offsets.
  if (cols < 1) { return null; }
  return {
    left: first.left,
    top: first.top,
    cols,
    cellW: cols > 1 ? rects[1].left - first.left : first.width,
    cellH: rects.length > cols ? rects[cols].top - first.top : first.height,
    tileW: first.width,
    tileH: first.height,
  };
}

/**
 * Confine a drag offset so the held tile cannot leave the grid. Without this the tile follows the
 * finger anywhere on screen and sails out over the rest of the dashboard.
 *
 * `slot` is the slot the tile started in, which is what its untransformed position is measured from.
 * The bounds are ordered defensively: a box measured mid-animation can come back narrower than a
 * tile, and an inverted range would otherwise push the tile the wrong way.
 */
export function clampOffsetToGrid(
  dx: number, dy: number, slot: number, m: GridMetrics, box: GridBox,
): { dx: number; dy: number } {
  const left = m.left + (slot % m.cols) * m.cellW;
  const top = m.top + Math.floor(slot / m.cols) * m.cellH;
  const minDx = -left;
  const minDy = -top;
  return {
    dx: unsigned(clamp(dx, minDx, Math.max(minDx, box.width - (left + m.tileW)))),
    dy: unsigned(clamp(dy, minDy, Math.max(minDy, box.height - (top + m.tileH)))),
  };
}

/**
 * The slot under a point. Row and column are resolved independently and each clamped into the
 * grid, then the result is clamped to the last real tile — so dragging into the empty tail of a
 * partial last row means "put it last" rather than snapping back to the row above.
 */
export function targetIndexFromPoint(x: number, y: number, m: GridMetrics, count: number): number {
  if (count <= 0) { return -1; }
  const rows = Math.ceil(count / m.cols);
  const row = clamp(Math.floor((y - m.top) / m.cellH), 0, rows - 1);
  const col = clamp(Math.floor((x - m.left) / m.cellW), 0, m.cols - 1);
  return Math.min(row * m.cols + col, count - 1);
}

/**
 * Where the tile that LIVES at `index` should appear while the tile from `from` is being held over
 * `to`. The dragged tile takes the hovered slot and everything the drag passed over shifts one slot
 * the other way, which is exactly the order the array ends up in when the drag is committed.
 */
export function displayIndex(index: number, from: number, to: number): number {
  if (index === from) { return to; }
  if (from < to && index > from && index <= to) { return index - 1; }
  if (from > to && index >= to && index < from) { return index + 1; }
  return index;
}

/**
 * The transform that moves the tile at `index` from its own slot to its mid-drag slot. Zero for the
 * dragged tile — the pointer positions that one — and zero for tiles the drag does not disturb.
 */
export function displacementFor(index: number, from: number, to: number, m: GridMetrics): { dx: number; dy: number } {
  if (index === from) { return { dx: 0, dy: 0 }; }
  const slot = displayIndex(index, from, to);
  return {
    dx: ((slot % m.cols) - (index % m.cols)) * m.cellW,
    dy: (Math.floor(slot / m.cols) - Math.floor(index / m.cols)) * m.cellH,
  };
}
